import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { Plus, User } from 'lucide-react';
import { ChooseSoundworldModal, ConfirmDeleteModal } from '../components/catalogue';
import { SoundworldProjectCard } from '../components/catalogue/SoundworldProjectCard';
import { useSoundworlds } from '../hooks/useSoundworlds';
import { useSoundworldProjects } from '../hooks/useSoundworldProjects';
import { useSoundworldProjectCreation } from '../hooks/useSoundworldProjectCreation';
import { useCredits } from '../contexts/CreditsContext';

export const ExploreSoundworld: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const username = localStorage.getItem("name") || "User";
    const { soundworlds, isLoading: soundworldsLoading } = useSoundworlds();
    const { projects, isLoading: projectsLoading, loadProjects, deleteProject } = useSoundworldProjects();
    const { createSoundworldProject, isCreating } = useSoundworldProjectCreation();
    const { credits } = useCredits();

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [trackName, setTrackName] = useState('');
    const [trackDescription, setTrackDescription] = useState('');
    const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }

        if (isAuthenticated) {
            loadProjects();
        }
    }, [isAuthenticated, isLoading, loadProjects, navigate]);

    const handleNewTrack = () => {
        // Reset form state
        setTrackName('');
        setTrackDescription('');
        // Open the Choose Soundworld modal
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTrackName('');
        setTrackDescription('');
    };

    const handleSoundworldSelect = async (soundworldId: string) => {
        try {
            // Find soundworld by ID to get the name
            const soundworld = soundworlds.find(sw => sw.id === soundworldId);
            if (!soundworld) {
                console.error('Soundworld not found');
                return;
            }

            // Create project with soundworld name
            const projectId = await createSoundworldProject(trackName, trackDescription, soundworld.name);

            if (projectId) {
                // Close modal and navigate to project
                handleCloseModal();
                navigate(`/generator/${projectId}`);
            }
        } catch (error) {
            console.error('Error creating project:', error);
        }
    };

    const handleContinueEditing = (projectId: string) => {
        navigate(`/generator/${projectId}`);
    };

    const handleDeleteClick = (projectId: string, projectName: string) => {
        setConfirmDelete({ id: projectId, name: projectName });
    };

    const handleDeleteProject = async (projectId: string) => {
        const success = await deleteProject(projectId);
        if (success) {
            setConfirmDelete(null);
            loadProjects(); // Перезагружаем список проектов
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Top Bar */ }
            <div className="w-full border-b border-border mt-[60px] md:mt-0">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="border flex gap-3 items-center border-border rounded-lg px-4 py-2">
                        <h1 className="text-lg font-semibold text-card-foreground">
                            Song Generator
                        </h1>
                    </div>

                    <div className="flex items-center gap-2">
                        <User size={ 16 } className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{ username }</span>
                        <div className="ml-2 px-3 py-1 rounded-full bg-muted text-sm font-medium text-muted-foreground">
                            { typeof credits === 'number' ? `${credits} credits` : '...' }
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Section - Song Generator */ }
            <div className="flex flex-col items-center justify-center pt-16 pb-16">
                <h1 className="text-4xl font-bold text-center mb-4 text-card-foreground">
                    Song Generator
                </h1>
                <p className="text-muted-foreground text-center mb-8 max-w-md">
                    Generate ideas and multi section instrumentals from a wide range of soundworlds.
                </p>
                <button
                    onClick={ handleNewTrack }
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus size={ 20 } />
                    New Track
                </button>
            </div>

            {/* Bottom Section - Previous Projects */ }
            <div className="px-8 pb-8 overflow-y-auto pr-2 max-h-[calc(100vh-360px)]">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-card-foreground">Previous Projects</h2>
                </div>

                { projectsLoading ? (
                    <div className="text-center py-8">
                        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto"></div>
                        <p className="text-muted-foreground mt-2">Loading projects...</p>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="text-center text-muted-foreground py-16">
                        <p className="text-sm">You have no projects yet. Start by creating a track.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        { projects.map((project) => (
                            <SoundworldProjectCard
                                key={ project.project_id }
                                project={ project }
                                onDelete={ (projectId) => handleDeleteClick(projectId, project.name) }
                                onContinue={ handleContinueEditing }
                            />
                        )) }
                    </div>
                ) }
            </div>

            {/* Choose Soundworld Modal */ }
            <ChooseSoundworldModal
                isOpen={ isModalOpen }
                onClose={ handleCloseModal }
                onSelect={ handleSoundworldSelect }
                trackName={ trackName }
                description={ trackDescription }
                onTrackNameChange={ setTrackName }
                onDescriptionChange={ setTrackDescription }
                isCreating={ isCreating }
            />

            {/* Delete Confirmation Modal */ }
            { confirmDelete && (
                <ConfirmDeleteModal
                    projectName={ confirmDelete.name }
                    onCancel={ () => setConfirmDelete(null) }
                    onConfirm={ () => handleDeleteProject(confirmDelete.id) }
                />
            ) }
        </div>
    );
};

